// src/components/settings/super-admin-tab.jsx
// Super Admin controls, rendered as a tab inside the Settings portal.
//   • Owner  (isSuperAdminOwner)      → edit the cap AND manage who's allowed.
//   • Guest  (id in allowedUserIds)   → edit the cap only.
// The tab is only shown to those users — gating lives in main-settings.jsx.
import { useEffect, useMemo, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  loadBrokerageSettings,
  saveBrokerageSettings,
  BROKERAGE_SETTINGS_FALLBACK,
  isSuperAdminOwner,
} from "../../utils/brokerageSettings";

export default function SuperAdminTab() {
  const [{ user, users, employees }] = useStateValue();

  const [loading, setLoading] = useState(true);
  const [annualCap, setAnnualCap] = useState(
    BROKERAGE_SETTINGS_FALLBACK.annualCap,
  );
  const [capInput, setCapInput] = useState("");
  const [allowedUserIds, setAllowedUserIds] = useState([]);
  const [savingCap, setSavingCap] = useState(false);

  const isOwner = isSuperAdminOwner(user);

  // Team list for the access card (prefer `users`, fall back to `employees`).
  const team = useMemo(() => {
    const src = (users && users.length ? users : employees) || [];
    return src
      .filter((u) => u && u.id)
      .map((u) => ({
        id: u.id,
        name:
          [u.firstname, u.lastname].filter(Boolean).join(" ") ||
          u.name ||
          u.email ||
          "Team Member",
        role: u.role || "",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, employees]);

  useEffect(() => {
    let cancelled = false;
    loadBrokerageSettings().then((s) => {
      if (cancelled) return;
      setAnnualCap(s.annualCap);
      setCapInput(String(s.annualCap));
      setAllowedUserIds(s.allowedUserIds || []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const usd = (n) =>
    "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

  const saveCap = async () => {
    const n = Number(String(capInput).replace(/[^0-9.]/g, ""));
    if (!n || n <= 0) {
      toast.warn("Enter a cap amount greater than 0");
      return;
    }
    setSavingCap(true);
    try {
      await saveBrokerageSettings({ annualCap: n }, user?.id);
      setAnnualCap(n);
      toast.success("Brokerage cap saved");
    } catch (e) {
      toast.error("Couldn't save the cap — try again");
    } finally {
      setSavingCap(false);
    }
  };

  const toggleAccess = async (memberId) => {
    if (!isOwner) return; // only the owner manages the list
    if (memberId === user?.id) return; // can't switch yourself off
    const prev = allowedUserIds;
    const next = prev.includes(memberId)
      ? prev.filter((id) => id !== memberId)
      : [...prev, memberId];
    setAllowedUserIds(next); // optimistic
    try {
      await saveBrokerageSettings({ allowedUserIds: next }, user?.id);
    } catch (e) {
      setAllowedUserIds(prev); // revert on failure
      toast.error("Couldn't update access — try again");
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Lock size={13} /> Private area — visible only to super admins.
      </div>

      {/* Brokerage Cap */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-1">
          Brokerage Cap
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          The annual cap each agent works toward. Feeds the dashboard's Progress
          to Cap bar and the split. Current:{" "}
          <b className="text-slate-700">{usd(annualCap)}</b>.
        </p>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Cap Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                $
              </span>
              <input
                type="number"
                min="0"
                value={capInput}
                onChange={(e) => setCapInput(e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-[#1E293B]"
                placeholder="70000"
              />
            </div>
          </div>
          <button
            onClick={saveCap}
            disabled={savingCap}
            className="px-5 py-2 text-sm rounded-lg bg-[#1E293B] text-white font-semibold disabled:opacity-60"
          >
            {savingCap ? "Saving…" : "Save Cap"}
          </button>
        </div>
      </div>

      {/* Who Can Access — owner only */}
      {isOwner && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            Who Can Access
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Choose who else can open this Super Admin area. Only you can change
            this list.
          </p>
          <div className="divide-y divide-gray-100">
            {team.map((m) => {
              const isSelf = m.id === user?.id;
              const allowed = isSelf || allowedUserIds.includes(m.id);
              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between py-2.5"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {m.name}
                      {isSelf && (
                        <span className="text-slate-400 font-normal"> · you</span>
                      )}
                    </div>
                    {m.role && (
                      <div className="text-[11px] text-slate-400">{m.role}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleAccess(m.id)}
                    disabled={isSelf}
                    title={isSelf ? "You always have access" : ""}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      allowed ? "bg-[#1E293B]" : "bg-gray-300"
                    } ${isSelf ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        allowed ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
            {team.length === 0 && (
              <p className="text-sm text-slate-400 py-3">
                No team members found.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
